/**
 * BMI Calculator — Light DOM web component.
 *
 * Calculation and circular gauge mirror the Webflow BMI calculator:
 * BMI = kg / m², four categories, a proportional 270° arc gauge with an
 * animated pointer. Input handling follows the Felix house style
 * (debounced input + count-up number); gauge timings match the original
 * (pointer 600ms ease-out cubic, segment fade 0.6s, pointer fade/colour 0.4s).
 */

const KG_PER_LB = 0.45359237
const CM_PER_IN = 2.54
const DEBOUNCE_MS = 350
const POINTER_MS = 600

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3)

// Match the live display: whole numbers show no decimal (27), fractional show one (22.2).
const fmtBmi = (n) => {
	const v = Math.round(n * 10) / 10
	return Number.isInteger(v) ? String(v) : v.toFixed(1)
}

class FelixBmiCalculator extends HTMLElement {
	connectedCallback() {
		this._timer = null
		this._isMetric = false
		this._hasCalculated = false
		this._reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
		this.cacheElements()
		this.resolveColors()
		this.initGauge()
		this.bindEvents()
		this.maybeShowPresets()
	}

	disconnectedCallback() {
		this.unbindEvents()
	}

	cacheElements() {
		const $ = (s) => this.querySelector(s)
		const $$ = (s) => [...this.querySelectorAll(s)]

		this.ft = $('[name="bmi-feet"]')
		this.inch = $('[name="bmi-inches"]')
		this.cm = $('[name="bmi-height-cm"]')
		this.lb = $('[name="bmi-pounds"]')
		this.kg = $('[name="bmi-weight-kg"]')

		this.imperialGroups = $$('[data-unit-group="imperial"]')
		this.metricGroups = $$('[data-unit-group="metric"]')
		this.switchLinks = $$('.form-input__switch-link')

		this.valueEl = $('[data-result="bmi-value"]')
		this.statusEl = $('[data-result="bmi-status"]')
		this.rows = $$('.bmi-range-item')
		this.cta = $('.bmi__cta')

		this.svg = $('#bmi-circular-gauge')
		this.baseG = $('#bmi-circular-base')
		this.fillG = $('#bmi-circular-fills')
		this.pointerG = $('#bmi-circular-pointer')

		this.numberInputs = $$('input[type="number"]')
	}

	resolveColors() {
		const css = getComputedStyle(this)
		const v = (name, fallback) => (css.getPropertyValue(name).trim() || fallback)
		const red = v('--wp--preset--color--red-40', '#B52617')
		const green = v('--wp--preset--color--sage-30', '#2D8D0C')
		this.neutral = v('--wp--preset--color--slate-20', '#31302F')

		// Category colours match the live gauge: healthy green, all others red.
		this.ranges = [
			{ key: 'underweight', min: 15, max: 18.5, color: red },
			{ key: 'healthy', min: 18.6, max: 24.9, color: green },
			{ key: 'overweight', min: 25, max: 29.9, color: red },
			{ key: 'obese', min: 30, max: 40, color: red },
		]
	}

	/* ========== Gauge geometry (ported from the Webflow embed) ========== */

	initGauge() {
		this.cx = 180
		this.cy = 180
		this.radius = 135
		this.arrowRadius = 158
		this.startDeg = 225
		this.totalDeg = 270
		this.gapDeg = 12
		this.minBMI = 15
		this.maxBMI = 40
		this.currentAngle = this.startDeg
		this.fills = []
		this.buildSegments()
		this.createPointer()
	}

	polar(r, deg) {
		const a = (deg * Math.PI) / -180
		return [this.cx + r * Math.cos(a), this.cy + r * Math.sin(a)]
	}

	arcPath(r, a0, a1) {
		const [x0, y0] = this.polar(r, a0)
		const [x1, y1] = this.polar(r, a1)
		const large = Math.abs(a1 - a0) > 180 ? 1 : 0
		return `M ${x0.toFixed(3)} ${y0.toFixed(3)} A ${r} ${r} 0 ${large} 1 ${x1.toFixed(3)} ${y1.toFixed(3)}`
	}

	makePath(group, d) {
		const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
		path.setAttribute('d', d)
		group.appendChild(path)
		return path
	}

	buildSegments() {
		if (!this.baseG || !this.fillG) return
		this.baseG.innerHTML = ''
		this.fillG.innerHTML = ''
		this.fills = []
		const totalRange = this.maxBMI - this.minBMI
		const availableArc = this.totalDeg - this.gapDeg * (this.ranges.length - 1)
		let currentDeg = this.startDeg

		this.ranges.forEach((range) => {
			const proportion = (range.max - range.min) / totalRange
			const segmentDeg = availableArc * proportion
			const start = currentDeg
			const end = start - segmentDeg
			const d = this.arcPath(this.radius, start, end)
			this.makePath(this.baseG, d)
			const fill = this.makePath(this.fillG, d)
			fill.style.stroke = range.color
			fill.style.opacity = '0'
			this.fills.push(fill)
			currentDeg = end - this.gapDeg
		})
	}

	createPointer() {
		if (!this.pointerG) return
		this.pointerG.innerHTML = ''
		this.pointerG.setAttribute('filter', 'url(#bmi-pointer-shadow)')
		const tri = document.createElementNS('http://www.w3.org/2000/svg', 'path')
		tri.setAttribute('d', 'M6.79904 9.75C6.22169 10.75 4.77831 10.75 4.20096 9.75L0.303847 3C-0.273504 2 0.448185 0.750002 1.60289 0.750002L9.39712 0.750001C10.5518 0.750001 11.2735 2 10.6962 3L6.79904 9.75Z')
		tri.setAttribute('fill', this.neutral)
		tri.setAttribute('stroke', this.neutral)
		tri.setAttribute('stroke-width', '1.5')
		this.pointerG.appendChild(tri)
		this.pointer = tri
		this.pointerG.style.opacity = '0'
		this.updatePointerPosition(this.startDeg)
	}

	bmiToDeg(bmi) {
		const clamped = Math.max(this.minBMI, Math.min(this.maxBMI, bmi))
		const progress = (clamped - this.minBMI) / (this.maxBMI - this.minBMI)
		return this.startDeg - progress * this.totalDeg
	}

	// Derive the lit segment from the SAME cutoffs as category() so the gauge
	// never desyncs from the number/legend at the 18.5/25/30 boundaries. The
	// ranges array still drives segment span geometry + colour.
	segmentIndex(bmi) {
		return this.ranges.findIndex((r) => r.key === this.category(bmi))
	}

	updatePointerPosition(angle) {
		if (!this.pointer) return
		const [x, y] = this.polar(this.arrowRadius, angle)
		this.pointer.setAttribute(
			'transform',
			`translate(${x.toFixed(3)},${y.toFixed(3)}) rotate(${90 - angle}) translate(-5.5, -5)`
		)
	}

	animatePointer(targetAngle) {
		if (this._reduceMotion) {
			this.updatePointerPosition(targetAngle)
			this.currentAngle = targetAngle
			return
		}
		const startAngle = this.currentAngle
		const delta = targetAngle - startAngle
		const t0 = performance.now()
		const run = (this._pointerRun = (this._pointerRun || 0) + 1)
		const step = (time) => {
			if (this._pointerRun !== run) return
			const p = Math.min((time - t0) / POINTER_MS, 1)
			const angle = startAngle + delta * easeOutCubic(p)
			this.updatePointerPosition(angle)
			this.currentAngle = angle
			if (p < 1) requestAnimationFrame(step)
		}
		requestAnimationFrame(step)
	}

	updateSegments(bmi) {
		const active = this.segmentIndex(bmi)
		this.fills.forEach((fill, i) => {
			fill.style.opacity = i === active ? '1' : '0'
		})
		const color = active >= 0 ? this.ranges[active].color : this.neutral
		if (this.pointer) {
			this.pointer.setAttribute('fill', color)
			this.pointer.setAttribute('stroke', color)
		}
	}

	updateGauge(bmi) {
		if (!bmi || bmi <= 0) {
			this.resetGauge()
			return
		}
		if (this.pointerG) this.pointerG.style.opacity = '1'
		this.updateSegments(bmi)
		this.animatePointer(this.bmiToDeg(bmi))
	}

	resetGauge() {
		// Cancel any in-flight pointer tween so it can't overwrite the reset position.
		this._pointerRun = (this._pointerRun || 0) + 1
		this.fills.forEach((f) => (f.style.opacity = '0'))
		if (this.pointerG) this.pointerG.style.opacity = '0'
		this.currentAngle = this.startDeg
		this.updatePointerPosition(this.startDeg)
	}

	/* ========== Calculation ========== */

	category(bmi) {
		if (bmi < 18.5) return 'underweight'
		if (bmi < 25) return 'healthy'
		if (bmi < 30) return 'overweight'
		return 'obese'
	}

	readyToCalc() {
		const haveH = this._isMetric ? +this.cm?.value > 0 : (+this.ft?.value > 0 || +this.inch?.value > 0)
		const haveW = this._isMetric ? +this.kg?.value > 0 : +this.lb?.value > 0
		return haveH && haveW
	}

	doCalc() {
		let hMeters, wKg

		if (this._isMetric) {
			hMeters = (+this.cm?.value || 0) / 100
			wKg = +this.kg?.value || 0
		} else {
			const ftv = +this.ft?.value || 0
			const inchv = +this.inch?.value || 0
			hMeters = (ftv * 12 + inchv) * CM_PER_IN / 100
			wKg = (+this.lb?.value || 0) * KG_PER_LB
		}

		const bmi = hMeters > 0 && wKg > 0 ? wKg / (hMeters * hMeters) : 0

		if (bmi > 0) {
			this.showResult(+bmi.toFixed(1))

			// Once per page view, and deliberately without the BMI value or the
			// height and weight behind it — all sensitive personal information.
			if (!this._hasCalculated) {
				window.felixTrack?.('calculator_complete', { calculatorName: 'bmi_calculator' })
			}

			this._hasCalculated = true
		} else {
			this.reset()
		}
	}

	showResult(bmi) {
		const cat = this.category(bmi)
		this.setNumber(bmi, cat)
		this.updateGauge(bmi)
		this.updateLegend(cat)
		this.toggleCta(bmi >= 25)
		this.announce(`Your BMI is ${fmtBmi(bmi)}, ${cat} range.`)
	}

	reset() {
		if (this.valueEl) {
			this.valueEl.textContent = ''
			this.valueEl.classList.remove('is-underweight', 'is-healthy', 'is-overweight', 'is-obese')
		}
		this.resetGauge()
		this.updateLegend(null)
		this.toggleCta(false)
		this.announce('')
	}

	setNumber(bmi, cat) {
		if (!this.valueEl) return
		this.valueEl.classList.remove('is-underweight', 'is-healthy', 'is-overweight', 'is-obese')
		this.valueEl.classList.add('is-' + cat)
		// Set instantly (no count-up), matching the Webflow original.
		this.valueEl.textContent = fmtBmi(bmi)
	}

	announce(message) {
		if (this.statusEl) this.statusEl.textContent = message
	}

	updateLegend(cat) {
		this.rows.forEach((row) => {
			const isActive = !!cat && row.dataset.category === cat
			row.classList.toggle('active', isActive)
			row.querySelector('.bmi-range-item__desc')?.setAttribute('aria-hidden', String(!isActive))
		})
	}

	toggleCta(show) {
		if (this.cta) this.cta.classList.toggle('show', show)
	}

	/* ========== Events ========== */

	bindEvents() {
		this._trigger = () => {
			clearTimeout(this._timer)
			this._timer = setTimeout(() => {
				if (this._hasCalculated || this.readyToCalc()) this.doCalc()
			}, DEBOUNCE_MS)
		}
		this._onSwitch = (e) => {
			e.preventDefault()
			this.setUnits(e.target.dataset.switchTo === 'metric')
			// After a result, the recalc announcement already implies the unit; only
			// announce the switch on its own when there's no result to recompute.
			if (this._hasCalculated) this.doCalc()
			else this.announce(`Switched to ${this._isMetric ? 'metric' : 'imperial'} units.`)
		}
		// Prevent scroll from changing number inputs.
		this._onWheel = (e) => e.preventDefault()

		const inputs = [this.ft, this.inch, this.cm, this.lb, this.kg]
		inputs.forEach((el) => {
			if (!el) return
			el.addEventListener('input', this._trigger)
			el.addEventListener('change', this._trigger)
		})
		this.numberInputs.forEach((el) => el.addEventListener('wheel', this._onWheel, { passive: false }))
		this.switchLinks.forEach((link) => link.addEventListener('click', this._onSwitch))
	}

	unbindEvents() {
		const inputs = [this.ft, this.inch, this.cm, this.lb, this.kg]
		inputs.forEach((el) => {
			if (!el) return
			el.removeEventListener('input', this._trigger)
			el.removeEventListener('change', this._trigger)
		})
		this.numberInputs.forEach((el) => el.removeEventListener('wheel', this._onWheel))
		this.switchLinks.forEach((link) => link.removeEventListener('click', this._onSwitch))
	}

	setUnits(metric) {
		this._isMetric = !!metric
		this.imperialGroups.forEach((g) => g.classList.toggle('hidden', this._isMetric))
		this.metricGroups.forEach((g) => g.classList.toggle('hidden', !this._isMetric))
	}

	/* ========== Debug presets (local & staging only) ========== */

	maybeShowPresets() {
		const host = location.hostname || ''
		const isLocal = host === 'localhost' || host.endsWith('.local') || host === '127.0.0.1'
		const isStaging = host === 'felixforus.ca' || host.endsWith('.felixforus.ca')
		if (!isLocal && !isStaging) return

		const wrap = document.createElement('div')
		wrap.className = 'bmi__presets mt-16'
		wrap.innerHTML =
			'<div class="form-input__label-row flex justify-between align-center mb-12">' +
				'<span class="form-input__label text-body-md text-slate-40">Debug presets (local &amp; staging only)</span>' +
			'</div>' +
			'<div class="bmi__presets-buttons flex gap-8 flex-wrap" role="group" aria-label="Debug presets">' +
			[
				['underweight', 'Underweight'],
				['healthy', 'Healthy'],
				['overweight', 'Overweight'],
				['obese', 'Obese'],
			]
				.map(
					([k, l]) =>
						`<button type="button" class="text-body-sm py-10 px-14 bg-slate-95 rounded-sm cursor-pointer flex-1" data-preset="${k}">${l}</button>`
				)
				.join('') +
			'</div>'

		this.querySelector('.bmi__form')?.appendChild(wrap)

		wrap.querySelector('.bmi__presets-buttons').addEventListener('click', (e) => {
			const btn = e.target.closest('[data-preset]')
			if (!btn) return
			const presets = {
				underweight: { ft: 5, inch: 9, lb: 110 },
				healthy: { ft: 5, inch: 9, lb: 150 },
				overweight: { ft: 5, inch: 9, lb: 185 },
				obese: { ft: 5, inch: 9, lb: 230 },
			}
			const d = presets[btn.dataset.preset]
			if (!d) return
			const sv = (el, val) => { if (el) el.value = val }
			sv(this.ft, d.ft)
			sv(this.inch, d.inch)
			sv(this.lb, d.lb)
			sv(this.cm, Math.round((d.ft * 12 + d.inch) * CM_PER_IN))
			sv(this.kg, Math.round(d.lb * KG_PER_LB))
			this.setUnits(false)
			this._hasCalculated = true
			this.doCalc()
		})
	}
}

if (!customElements.get('felix-bmi-calculator')) {
	customElements.define('felix-bmi-calculator', FelixBmiCalculator)
}
